import { useCallback, useEffect, useState } from 'react';

/**
 * The whole router. Real paths rather than hashes, because invite links have
 * to survive being pasted into WhatsApp, and the server already falls back to
 * index.html for anything that isn't /api.
 */
export function useRoute(): { path: string; go: (to: string) => void } {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((to: string) => {
    // Sets state even when the URL already matches. The invite flow calls
    // replaceState('/') to strip the token and then go('/'); returning early
    // there left React rendering the invitation screen over a signed-in
    // session until the user thought to refresh.
    window.history.pushState({}, '', to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);

  return { path, go };
}

/**
 * The invitation token, read from the URL fragment.
 *
 * `https://setu…/invite#<token>` rather than `/invite/<token>`: a fragment is
 * stripped by the browser before the request is sent, so the token never
 * reaches Cloudflare, any proxy, or a server access log. It is still in
 * history until the accept screen replaces the URL.
 */
export function useInviteToken(path: string): string | null {
  return useLinkToken(path, '/invite');
}

/** The same, for the forgot-password link: `/reset#<token>`. */
export function useResetToken(path: string): string | null {
  return useLinkToken(path, '/reset');
}

function useLinkToken(path: string, prefix: string): string | null {
  const [hash, setHash] = useState(() => window.location.hash);

  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (!path.startsWith(prefix)) return null;
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}
